import { LOCALE_ID, NgModule } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeHu from '@angular/common/locales/hu';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

import { AppComponent } from './app.component';
import { BottomBarComponent } from './bottom-bar/bottom-bar.component';
import { SideBarComponent } from './side-bar/side-bar.component';
import { TopBarComponent } from './top-bar/top-bar.component';
import { ExamAddressListComponent } from './views/exam-address/exam-address-list/exam-address-list.component';
import { ExamAddressEditComponent } from './views/exam-address/exam-address-edit/exam-address-edit.component';
import { ExamReportListComponent } from './views/exam-report/exam-report-list/exam-report-list.component';
import { ExamReportEditComponent } from './views/exam-report/exam-report-edit/exam-report-edit.component';
import { ExamReportFormComponent } from './components/forms/exam-report-form/exam-report-form.component';
import { ExaminerListComponent } from './views/examiner/examiner-list/examiner-list.component';
import { UserListComponent } from './views/user/user-list/user-list.component';
import { UserNewComponent } from './views/user/user-new/user-new.component';
import { ExamAddressFormComponent } from './components/forms/exam-address-form/exam-address-form.component';
import { ExamAddressNewComponent } from './views/exam-address/exam-address-new/exam-address-new.component';
import { ExaminerNewComponent } from './views/examiner/examiner-new/examiner-new.component';
import { ExamReportNewComponent } from './views/exam-report/exam-report-new/exam-report-new.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HomeLayoutComponent } from './layouts/home-layout';
import { ErrorLayoutComponent } from './layouts/error-layout';
import { DatepickerDirective } from './directives/datepicker/datepicker.directive';
import { DatetimepickerDirective } from './directives/datetimepicker/datetimepicker.directive';
import {
  API_BASE_URL,
  ExamAddressesClient,
  ExaminersClient,
  ExamReportsClient,
  ExamSubjectsClient,
  StudentResultsClient,
  StudentsClient,
  SubjectsClient,
  TenantsClient,
  UsersClient,
} from './generated-client/generated-api-client';
import { LoaderComponent } from './components/loader/loader/loader.component';
import { ExaminerFormComponent } from './components/forms/examiner-form/examiner-form.component';
import { ExamReportDetailsTabComponent } from './views/exam-report/exam-report-tabs/exam-report-details-tab/exam-report-details-tab.component';
import { ExamReportExaminersTabComponent } from './views/exam-report/exam-report-tabs/exam-report-examiners-tab/exam-report-examiners-tab.component';
import { ExamReportStudentsTabComponent } from './views/exam-report/exam-report-tabs/exam-report-students-tab/exam-report-students-tab.component';
import { ExamReportResultsTabComponent } from './views/exam-report/exam-report-tabs/exam-report-results-tab/exam-report-results-tab.component';
import { StudentResultsDatatableComponent } from './components/datatables/student-results-datatable/student-results-datatable.component';
import {
  DataTableDataService,
  isFormModifiedDataService,
  SimpleNavigationPathDataService,
  SimpleNavigationService,
} from './services/data-services/navigation-data-services';
import { DatatableComponent } from './components/datatables/datatable/datatable/datatable.component';
import { DefaultSimpleModalOptionConfig, defaultSimpleModalOptions, SimpleModalModule } from 'ngx-simple-modal';
import { ConfirmModalComponent } from './components/modals/confirm-modal/confirm-modal.component';
import { AddExaminerModalComponent } from './components/modals/add-examiner-modal/add-examiner-modal.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { ExamResultsDatatableComponent } from './components/datatables/exam-results-datatable/exam-results-datatable.component';
import { StudentExamDataService } from './services/data-services/student-exam-data.service';
import { StudentNewComponent } from './views/student/student-new/student-new.component';
import { StudentFormComponent } from './components/forms/student-form/student-form/student-form.component';
import { StudentEditComponent } from './views/student/student-edit/student-edit.component';
import { ToastrModule } from 'ngx-toastr';
import { StudentsDatatableComponent } from './components/datatables/students-datatable/students-datatable.component';
import { PdfPreviewModalComponent } from './components/modals/pdf-preview-modal/pdf-preview-modal.component';
import { ExamSubjectDatatableComponent } from './components/datatables/exam-subject-datatable/exam-subject-datatable.component';
import { AddExamSubjectModalComponent } from './components/modals/add-exam-subject-modal/add-exam-subject-modal.component';
import { ExamDateShiftConfirmModalComponent } from './components/modals/exam-date-shift-confirm-modal/exam-date-shift-confirm-modal.component';
import { SyncDataService } from './services/data-services/sync-data.service';
import { ExamReportDataService } from './services/data-services/exam-report-data.service';
import { AuthConfigModule } from './auth/auth-config.module';
import { AppConfigService } from './services/app-config.service';
import { AuthHelperService } from './services/auth-helper.service';
import { HomeComponent } from './components/home/home.component';
import { ErrorComponent } from './components/error/error.component';
import { ErrorProcessorService } from './services/error-processor.service';
import { EmptyComponent } from './components/empty/empty.component';
import { TenantListComponent } from './views/tenant/tenant-list/tenant-list.component';
import { TenantNewComponent } from './views/tenant/tenant-new/tenant-new.component';
import { TenantEditComponent } from './views/tenant/tenant-edit/tenant-edit.component';
import { TenantFormComponent } from './components/forms/tenant-form/tenant-form.component';
import { TenantInterceptor } from './interceptors/tenant.interceptor';
import { UserFormComponent } from './components/forms/user-form/user-form.component';
import { UserEditComponent } from './views/user/user-edit/user-edit.component';
import { MissingTenantComponent } from './components/missing-tenant/missing-tenant.component';
import { ConfirmReasonModalComponent } from './components/modals/archive-confirm-modal/confirm-reason-modal.component';
import { StudentImportModalComponent } from './components/modals/student-import-modal/student-import-modal.component';
import { SubjectListComponent } from './views/subject/subject-list/subject-list.component';
import { SubjectEditComponent } from './views/subject/subject-edit/subject-edit.component';
import { SubjectFormComponent } from './components/forms/subject-form/subject-form.component';
import { DetailedResultV2PreviewModalComponent } from './components/modals/detailed-resultV2-preview-modal/detailed-resultV2-preview-modal.component';
import { StudentChangesModalComponent } from './components/modals/student-changes-modal/student-changes-modal.component';
import { StudentEventsModalComponent } from './components/modals/student-events-modal/student-events-modal.component';
import { PersistentDataService } from './services/persistent-data.service';

registerLocaleData(localeHu);

export function getApiBaseUrl(): string {
  return AppConfigService.appConfig.apiUrl;
}

@NgModule({
  declarations: [
    AppComponent,
    HomeLayoutComponent,
    ErrorLayoutComponent,
    BottomBarComponent,
    SideBarComponent,
    TopBarComponent,
    ExamAddressListComponent,
    ExamAddressEditComponent,
    ExamReportListComponent,
    ExamReportEditComponent,
    ExamReportFormComponent,
    ExaminerListComponent,
    UserListComponent,
    UserNewComponent,
    ExamAddressFormComponent,
    ExamAddressNewComponent,
    ExaminerNewComponent,
    ExamReportNewComponent,
    DatepickerDirective,
    DatetimepickerDirective,
    LoaderComponent,
    DatatableComponent,
    ConfirmModalComponent,
    AddExaminerModalComponent,
    ExaminerFormComponent,
    ExamReportDetailsTabComponent,
    ExamReportExaminersTabComponent,
    ExamReportStudentsTabComponent,
    ExamReportResultsTabComponent,
    StudentResultsDatatableComponent,
    ExamResultsDatatableComponent,
    StudentNewComponent,
    StudentFormComponent,
    StudentEditComponent,
    StudentsDatatableComponent,
    PdfPreviewModalComponent,
    ExamSubjectDatatableComponent,
    AddExamSubjectModalComponent,
    ExamDateShiftConfirmModalComponent,
    HomeComponent,
    ErrorComponent,
    EmptyComponent,
    TenantListComponent,
    TenantNewComponent,
    TenantEditComponent,
    TenantFormComponent,
    UserFormComponent,
    UserEditComponent,
    MissingTenantComponent,
    ConfirmReasonModalComponent,
    StudentImportModalComponent,
    SubjectListComponent,
    SubjectEditComponent,
    SubjectFormComponent,
    StudentChangesModalComponent,
    DetailedResultV2PreviewModalComponent,
    StudentEventsModalComponent,
  ],
  imports: [
    HttpClientModule,
    AuthConfigModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    FormsModule,
    SimpleModalModule,
    NgSelectModule,
    ToastrModule.forRoot(),
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'hu-HU' },
    Title,
    HttpClientModule,
    { provide: API_BASE_URL, useFactory: getApiBaseUrl },
    ExamAddressesClient,
    ExamReportsClient,
    UsersClient,
    StudentsClient,
    ExaminersClient,
    StudentsClient,
    StudentResultsClient,
    ExamSubjectsClient,
    TenantsClient,
    SimpleNavigationPathDataService,
    SimpleNavigationService,
    DataTableDataService,
    isFormModifiedDataService,
    StudentExamDataService,
    SyncDataService,
    ExamReportDataService,
    ErrorProcessorService,
    SubjectsClient,
    PersistentDataService,
    {
      provide: DefaultSimpleModalOptionConfig,
      useValue: { ...defaultSimpleModalOptions, ...{ closeOnEscape: true, closeOnClickOutside: true } },
    },
    AuthHelperService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
